import { DatePicker } from "./DatePicker";
import { TagPicker } from "./TagPicker";
import { ApprovalPicker, NO_APPROVAL } from "./Approval";
import { useRef, useState, type ButtonHTMLAttributes } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as Popover from "@radix-ui/react-popover";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ProjectIcon } from "./ProjectIcon";
import { unwrap } from "@tern/api-client";
import { CalendarDays, ChevronRight, Layers, Maximize2, Minimize2, MoreHorizontal, X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { PRIORITIES } from "@/lib/issues";
import { liveStates, liveTasks, useProjectStore, type ProjectData } from "@/lib/store";
import { toast } from "@/lib/toast";
import { Picker } from "./Picker";
import { Avatar, PriorityIcon, StateIcon } from "./PropertyIcons";
import { MarkdownEditor, type MarkdownEditorHandle } from "./Markdown";

export type IssueDefaults = { state_id?: string; priority?: number; assignee_id?: string | null; label_ids?: string[]; parent_id?: string; task_id?: string };

export function IssueCreateDialog({ d, open, onOpenChange, defaults }: { d: ProjectData; open: boolean; onOpenChange: (o: boolean) => void; defaults?: IssueDefaults }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/55" />
        {open && <IssueCreateForm d={d} onOpenChange={onOpenChange} defaults={defaults} />}
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Mounted only while the dialog is open, so every opening starts from `defaults`. */
function IssueCreateForm({ d, onOpenChange, defaults }: { d: ProjectData; onOpenChange: (o: boolean) => void; defaults?: IssueDefaults }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const titleRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<MarkdownEditorHandle>(null);
  const submitting = useRef(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [stateId, setStateId] = useState<string | undefined>(
    () => defaults?.state_id ?? liveStates(d).find((s) => s.is_default)?.id ?? liveStates(d).find((s) => s.type === "unstarted")?.id,
  );
  const [priority, setPriority] = useState(defaults?.priority ?? 0);
  const [assignee, setAssignee] = useState<string | null>(defaults?.assignee_id ?? null);
  const [labels, setLabels] = useState<string[]>(defaults?.label_ids ?? []);
  const [taskId, setTaskId] = useState<string | null>(defaults?.task_id ?? null);
  const [due, setDue] = useState("");
  const [estimate, setEstimate] = useState<number | null>(null);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [approval, setApproval] = useState(NO_APPROVAL);
  const [editorBusy, setEditorBusy] = useState(false);
  const [tagBusy, setTagBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const state = stateId ? d.states[stateId] : undefined;
  const user = assignee ? d.users[assignee] : undefined;
  const task = taskId ? d.tasks[taskId] : undefined;
  const extraCount = Number(!!due) + Number(d.project.estimate_enabled && estimate != null);
  const shortcut = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘ ↵" : "Ctrl ↵";

  const submit = async (createMore: boolean) => {
    if (!title.trim() || submitting.current || editorBusy || tagBusy) return;
    submitting.current = true;
    setBusy(true);
    try {
      const issue = unwrap(
        await api.POST("/api/workspaces/{slug}/projects/{key}/issues", {
          params: { path: { slug: d.slug, key: d.project.key } },
          body: {
            ...approval,
            title: title.trim(),
            description: description || null,
            state_id: stateId ?? null,
            priority,
            assignee_id: assignee,
            label_ids: labels,
            due_date: due || null,
            estimate: d.project.estimate_enabled ? estimate : null,
            parent_id: defaults?.parent_id ?? null,
            task_id: taskId,
          },
        }),
      );
      useProjectStore.getState().applyIssue(issue);
      const id = `${d.project.key}-${issue.number}`;
      toast(t("issue.created", { id }), { action: { label: t("common.open"), run: () => void navigate({ to: "/$ws/issue/$identifier", params: { ws: d.slug, identifier: id } }) } });
      setTitle("");
      setDescription("");
      setDue("");
      if (createMore) {
        requestAnimationFrame(() => titleRef.current?.focus());
      } else {
        onOpenChange(false);
      }
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <Dialog.Content
      className={`fixed left-1/2 z-50 flex max-w-[calc(100vw-32px)] -translate-x-1/2 flex-col overflow-hidden rounded-[20px] border border-border-strong/80 bg-elevated shadow-[0_32px_120px_rgba(0,0,0,0.55)] ${expanded ? "top-6 h-[calc(100dvh-48px)] w-[1120px]" : "top-[12vh] max-h-[calc(88dvh-24px)] w-[920px]"}`}
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        titleRef.current?.focus();
      }}
      onEscapeKeyDown={(e) => busy && e.preventDefault()}
      onPointerDownOutside={(e) => busy && e.preventDefault()}
      onKeyDown={(e) => {
        if (e.defaultPrevented || e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          void submit(more);
        }
      }}
    >
      <div className="flex shrink-0 items-center gap-2.5 px-7 pt-5 pb-5 text-[13px]">
        <span className="inline-flex h-7 shrink-0 items-center gap-2 rounded-full border border-border bg-active/50 px-2.5 text-[12px] font-medium text-fg" title={d.project.name}>
          <ProjectIcon project={d.project} size={15} />
          {d.project.key}
        </span>
        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-faint" />
        <Dialog.Title className="truncate font-medium text-muted">{defaults?.parent_id ? t("issue.newSubIssue") : t("nav.newIssue")}</Dialog.Title>
        <Dialog.Description className="sr-only">{t("issue.composerHint")}</Dialog.Description>
        <div className="ml-auto flex shrink-0 items-center gap-1 text-muted">
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-hover hover:text-fg"
            title={t(expanded ? "issue.collapseComposer" : "issue.expandComposer")}
            aria-label={t(expanded ? "issue.collapseComposer" : "issue.expandComposer")}
            aria-pressed={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <Dialog.Close disabled={busy} className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-hover hover:text-fg disabled:opacity-50" title={t("issue.closeComposer")} aria-label={t("issue.closeComposer")}>
            <X className="h-4 w-4" />
          </Dialog.Close>
        </div>
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-y-contain">
        <fieldset disabled={busy} className="flex min-h-full min-w-0 flex-col px-7 pb-5 disabled:opacity-70">
          <div className="shrink-0">
            <input
              ref={titleRef}
              className="w-full border-0 bg-transparent py-1 text-[22px] leading-8 font-medium tracking-[-0.02em] text-fg outline-none placeholder:text-faint focus-visible:outline-none"
              aria-label={t("issue.titlePlaceholder")}
              placeholder={t("issue.titlePlaceholder")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
                if (e.key === "Enter" && !e.metaKey && !e.ctrlKey && !e.altKey) {
                  e.preventDefault();
                  descriptionRef.current?.focus();
                }
              }}
            />
            <div className="mt-2">
              <MarkdownEditor
                onBusyChange={setEditorBusy}
                projectId={d.project.id}
                ref={descriptionRef}
                variant="plain"
                placeholder={t("issue.descriptionPlaceholder")}
                value={description}
                onChange={setDescription}
                onSave={() => void submit(more)}
                minRows={expanded ? 9 : 5}
                disabled={busy}
              />
            </div>
          </div>

          <div className="mt-auto flex shrink-0 flex-wrap items-center gap-2 pt-5">
            <ApprovalPicker d={d} value={approval} onChange={(config) => { setApproval(config); if (config.approval_required && state?.type === "completed") setStateId(liveStates(d).find((s) => s.type === "done")?.id); }} disabled={busy} />
            <Picker
              value={stateId}
              placeholder={t("issue.changeStatus")}
              items={liveStates(d).filter((s) => !approval.approval_required || s.type !== "completed").map((s) => ({ id: s.id, label: s.name, icon: <StateIcon state={s} /> }))}
              onSelect={setStateId}
              trigger={<PropertyChip title={t("issue.status")}><StateIcon state={state} /><span className="truncate">{state?.name ?? t("issue.status")}</span></PropertyChip>}
            />
            <Picker
              value={String(priority)}
              placeholder={t("issue.changePriority")}
              items={PRIORITIES.map((p) => ({ id: String(p.value), label: t(`priority.${p.key}`), icon: <PriorityIcon priority={p.value} /> }))}
              onSelect={(value) => setPriority(Number(value))}
              trigger={<PropertyChip title={t("issue.priority")}><PriorityIcon priority={priority} /><span className="truncate">{t(`priority.${PRIORITIES[priority]?.key}`)}</span></PropertyChip>}
            />
            <Picker
              value={assignee ?? "none"}
              placeholder={t("issue.assignTo")}
              items={[{ id: "none", label: t("issue.unassigned"), icon: <Avatar size={16} /> }, ...Object.values(d.users).map((u) => ({ id: u.id, label: u.name, icon: <Avatar user={u} size={16} /> }))]}
              onSelect={(value) => setAssignee(value === "none" ? null : value)}
              trigger={<PropertyChip title={t("issue.assignee")}><Avatar user={user} size={16} /><span className="truncate">{user?.name ?? t("issue.assignee")}</span></PropertyChip>}
            />
            <Picker
              value={taskId ?? "none"}
              placeholder={t("issue.setTask")}
              items={[{ id: "none", label: t("issue.noTask"), icon: <Layers className="h-3.5 w-3.5" /> }, ...liveTasks(d).map((item) => ({ id: item.id, label: item.name, icon: <Layers className="h-3.5 w-3.5" /> }))]}
              onSelect={(value) => setTaskId(value === "none" ? null : value)}
              trigger={<PropertyChip title={task?.name ?? t("issue.task")}><Layers className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{task?.name ?? t("issue.task")}</span></PropertyChip>}
            />
            <TagPicker d={d} value={labels} onChange={setLabels} onBusyChange={setTagBusy} disabled={busy} />
            <Popover.Root>
              <Popover.Trigger asChild>
                <PropertyChip title={t("issue.moreProperties")} aria-label={t("issue.moreProperties")}>
                  <MoreHorizontal className="h-4 w-4" />
                  {extraCount > 0 && <span className="h-1.5 w-1.5 rounded-full bg-accent" />}
                </PropertyChip>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content align="start" sideOffset={8} collisionPadding={12} className="z-50 w-[280px] rounded-xl border border-border-strong bg-elevated p-4 shadow-xl" onKeyDown={(e) => e.stopPropagation()}>
                  <div className="mb-3 text-[12px] font-medium text-muted">{t("issue.moreProperties")}</div>
                  <div className="grid gap-2 text-[12px] text-muted">
                    <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{t("issue.dueDate")}</span>
                    <DatePicker label={t("issue.dueDate")} value={due} onChange={setDue} disabled={busy} boundary="end" />
                  </div>
                  {d.project.estimate_enabled && (
                    <label className="mt-4 grid gap-2 text-[12px] text-muted">
                      <span>{t("issue.estimate")}</span>
                      <select className="input h-9 py-0" value={estimate ?? "none"} disabled={busy} onChange={(e) => setEstimate(e.target.value === "none" ? null : Number(e.target.value))}>
                        <option value="none">{t("issue.noEstimate")}</option>
                        {[0, 1, 2, 3, 5, 8, 13].map((value) => <option key={value} value={value}>{value}</option>)}
                      </select>
                    </label>
                  )}
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          </div>
        </fieldset>
      </div>

      <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-5 gap-y-3 border-t border-border bg-sidebar/25 px-7 py-4">
        <label className="inline-flex cursor-pointer items-center gap-2.5 text-[12px] text-muted">
          <span className="relative inline-flex h-[18px] w-8 shrink-0 items-center">
            <input type="checkbox" role="switch" className="peer sr-only" checked={more} disabled={busy} onChange={(e) => setMore(e.target.checked)} />
            <span className="absolute inset-0 rounded-full border border-border-strong bg-active transition-colors peer-checked:border-accent peer-checked:bg-accent peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent peer-disabled:opacity-50" />
            <span className="pointer-events-none absolute left-[3px] h-3 w-3 rounded-full bg-fg shadow-sm transition-transform peer-checked:translate-x-[14px]" />
          </span>
          <span>{t("issue.createMore")}</span>
        </label>
        <button type="button" className="btn btn-primary h-9 gap-3 rounded-full px-4 text-[13px]" disabled={!title.trim() || busy || editorBusy || tagBusy} aria-busy={busy} onClick={() => void submit(more)}>
          {t(busy ? "issue.creating" : "issue.create")}
          <span className="text-[11px] font-normal text-accent-fg/70" aria-hidden="true">{shortcut}</span>
        </button>
      </div>
    </Dialog.Content>
  );
}

function PropertyChip({ className = "", ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type="button" className={`inline-flex h-8 max-w-[220px] items-center gap-2 rounded-full border border-border bg-active/50 px-2.5 text-[12px] text-muted transition-colors hover:bg-hover hover:text-fg ${className}`} {...props} />;
}
